
class BusinessLicenseSeasonCell extends UITableViewCell
{
    private nameLabel:MUILabel = null;
    private typeLabel:MUILabel = null;
    private seasonLabel:MUILabel = null;

    private df = ( MUIWebApplication.sharedInstance().delegate as AppDelegate ).dateFormatter;

    awakeFromHTML(){
        this.nameLabel = MUIOutlet(this, "name-lbl", "MUILabel");
        this.typeLabel = MUIOutlet(this, "type-lbl", "MUILabel");
        this.seasonLabel = MUIOutlet(this, "season-lbl", "MUILabel");
        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    set item(l:License){
        this.nameLabel.text = l.product?.name ?? "UNKNOWN";
        this.typeLabel.text = l.type?.name ?? "-";

        if (l.seasonal == false) {
            this.seasonLabel.text = MIOLocalizeString("NOT_SEASONAL", "NOT SEASONAL");
            return;
        }
        let since = l.seasonStartDate != null ? " · " + this.df.stringFromDate( l.seasonStartDate ) : "";
        this.seasonLabel.text = ( l.offSeason ? MIOLocalizeString("OFF_SEASON", "OFF SEASON") : MIOLocalizeString("SEASON_OPEN", "OPEN") ) + since
                              + " · " + SeasonMonths.count( l.seasonMonths ) + " " + MIOLocalizeString("MONTHS", "MONTHS");
    }
}

// The season of the active licences of a business at once: the same options as the licence
// card (SEASONAL, SEASON MONTHS) applied to the licences ticked below (all of them to begin
// with), and START / END of their season in one call (one invoice). START and END apply the
// options first, so a venue is set up in one go: SEASONAL on, its months, END SEASON.
class BusinessLicenseSeasonViewController extends MUIViewController
{
    static newInstance() : BusinessLicenseSeasonViewController {
        let vc = new BusinessLicenseSeasonViewController("business-license-season-view");
        vc.initWithResource("layout/licenses/BusinessLicenseSeasonView.html");
        return vc;
    }

    get preferredContentSize(): MIOSize {
        return new MIOSize(640, 600);
    }

    business:BusinessLicense = null;
    onDone:() => void = null;

    private cancelButton:MUIButton = null;
    private applyButton:MUIButton = null;
    private seasonalSwitch:MUISwitchButton = null;
    private seasonMonthsButton:MUIButton = null;
    private startButton:MUIButton = null;
    private endButton:MUIButton = null;
    private tableView:UITableView = null;

    private licenses:License[] = [];
    private selected:License[] = [];
    private seasonal = true;
    private months = SeasonMonths.ALL;

    viewDidLoad(){
        super.viewDidLoad();

        this.cancelButton = MUIOutlet(this, "cancel-btn", "MUIButton");
        this.cancelButton.setAction(this, function(this:BusinessLicenseSeasonViewController){
            this.dismissViewController(true);
        });

        this.applyButton = MUIOutlet(this, "apply-btn", "MUIButton");
        this.applyButton.setAction(this, function(this:BusinessLicenseSeasonViewController){
            this.applyAndSave( () => this.finish() );
        });

        this.seasonalSwitch = MUIOutlet(this, "seasonal-sw", "MUISwitchButton");
        this.seasonalSwitch.setOnChangeValue(this, function(this:BusinessLicenseSeasonViewController, control:MUISwitchButton, value:boolean){
            this.seasonal = value;
            this.updateOptions();
        });

        this.seasonMonthsButton = MUIOutlet(this, "season-months-btn", "MUIButton");
        this.seasonMonthsButton.setAction(this, function(this:BusinessLicenseSeasonViewController){
            if (this.seasonal == false) return;
            let vc = SeasonMonthsSelectViewController.newInstance();
            vc.months = this.months;
            vc.onChange = ( months:number ) => {
                this.months = months;
                this.updateOptions();
            };
            vc.modalPresentationStyle = MUIModalPresentationStyle.Popover;
            let pc = vc.popoverPresentationController;
            pc.sourceRect = this.seasonMonthsButton.frame;
            pc.sourceView = this.seasonMonthsButton;
            this.presentViewController( vc, true );
        });

        this.startButton = MUIOutlet(this, "start-btn", "MUIButton");
        this.startButton.tag = 1;
        this.startButton.setAction(this, function(this:BusinessLicenseSeasonViewController){
            this.season( this.startButton );
        });

        this.endButton = MUIOutlet(this, "end-btn", "MUIButton");
        this.endButton.tag = 2;
        this.endButton.setAction(this, function(this:BusinessLicenseSeasonViewController){
            this.season( this.endButton );
        });

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
    }

    viewWillAppear(animated?){
        super.viewWillAppear(animated);

        let sort = [ MIOSortDescriptor.sortDescriptorWithKey("product.name", true) ];
        DBHelper.queryObjectsWithCompletion( "License", sort, MIOPredicate.predicateWithFormat("deletedAt = null and active == true and business = " + this.business.identifier), ["product", "type"], this, function(this:BusinessLicenseSeasonViewController, objects:License[]){
            this.licenses = objects ?? [];
            this.selected = this.licenses.slice();
            // SEASONAL on (what the screen is for, CANCEL leaves everything as it was), with the
            // months of the first seasonal licence, if any
            let first = this.licenses.find( l => l.seasonal );
            this.seasonal = true;
            this.months = first?.seasonMonths ?? SeasonMonths.ALL;
            this.updateOptions();
            this.tableView.reloadData();
        });
    }

    private updateOptions(){
        this.seasonalSwitch.on = this.seasonal;
        this.seasonMonthsButton.title = this.seasonal ? SeasonMonths.count( this.months ) + " " + MIOLocalizeString("MONTHS", "MONTHS") : "-";
    }

    // The options on the ticked licences. Unmarking a licence off season is skipped: it has
    // no renewal date and would never renew again (start its season first). Returns the skipped.
    private applyOptions() : License[] {
        let skipped:License[] = [];
        for (let l of this.selected) {
            if (this.seasonal == false) {
                if (l.seasonal && l.offSeason) { skipped.push( l ); continue; }
                if (l.seasonal) l.seasonal = false;
                continue;
            }
            // Marked while it works: its season is open, END SEASON closes it
            if (l.seasonal == false) {
                l.seasonal = true;
                l.offSeason = false;
            }
            if (l.seasonMonths != this.months) l.seasonMonths = this.months;
        }
        return skipped;
    }

    // Sync mode: the completion comes once the server has it
    private applyAndSave( then:() => void ){
        if (this.selected.length == 0) {
            AppHelper.showErrorMessage( this, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("SELECT_AT_LEAST_ONE_LICENSE", "SELECT AT LEAST ONE LICENSE") );
            return;
        }
        let skipped = this.applyOptions();
        DBHelper.saveMainContextWithCompletion( this, function(this:BusinessLicenseSeasonViewController){
            this.tableView.reloadData();
            if (skipped.length > 0) {
                let names = skipped.map( l => l.product?.name ?? "?" ).join( ", " );
                AppHelper.showInfoMessage( this, MIOLocalizeString("INFO", "INFO"), MIOLocalizeString("OFF_SEASON_LICENSES_STAY_SEASONAL", "OFF SEASON LICENSES STAY SEASONAL, START THEIR SEASON FIRST") + ": " + names );
            }
            then();
        });
    }

    // START: the ticked licences off season; END: the ticked ones in season. The day is asked after applying the options.
    private season( button:MUIButton ){
        if (this.seasonal == false) {
            AppHelper.showErrorMessage( this, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("SWITCH_SEASONAL_ON_FIRST", "SWITCH SEASONAL ON FIRST") );
            return;
        }
        this.applyAndSave( () => {
            let start = button.tag == 1;
            let targets = this.selected.filter( l => l.seasonal && l.offSeason == start );
            if (targets.length == 0) {
                AppHelper.showErrorMessage( this, MIOLocalizeString("ERROR", "ERROR"), start
                    ? MIOLocalizeString("NO_TICKED_LICENSE_IS_OFF_SEASON", "NO TICKED LICENSE IS OFF SEASON")
                    : MIOLocalizeString("NO_TICKED_LICENSE_IS_IN_SEASON", "NO TICKED LICENSE IS IN SEASON") );
                return;
            }
            this.seasonTargets = targets;

            let dp = new MUIDatePickerController();
            dp.init();
            dp.delegate = this;
            dp.tag = button.tag;
            dp.modalPresentationStyle = MUIModalPresentationStyle.Popover;
            let pc = dp.popoverPresentationController;
            pc.sourceRect = button.frame;
            pc.sourceView = button;
            this.presentViewController( dp, true );
        });
    }

    private seasonTargets:License[] = [];
    didSelectDate(controller:MUIDatePickerController, date:Date){
        if (controller.tag == 1) LicenseSeasonActions.start( this, this.business, this.seasonTargets, date, () => this.finish() );
        else LicenseSeasonActions.end( this, this.business, this.seasonTargets, date, () => this.finish() );
    }

    private finish(){
        if (this.onDone != null) this.onDone();
        this.dismissViewController( true );
    }

    numberOfSections(tableView:UITableView){
        return 1;
    }

    numberOfRowsInSection(tableView:UITableView, section:number){
        return this.licenses.length;
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        let cell = tableView.dequeueReusableCellWithIdentifier("LicenseSeasonCell") as BusinessLicenseSeasonCell;
        let l = this.licenses[ indexPath.row ];
        cell.item = l;
        cell.accessoryType = this.selected.indexOf( l ) >= 0 ? UITableViewCellAccessoryType.Checkmark : UITableViewCellAccessoryType.None;
        return cell;
    }

    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        let l = this.licenses[ indexPath.row ];
        let i = this.selected.indexOf( l );
        if (i >= 0) this.selected.splice( i, 1 );
        else this.selected.push( l );

        let cell = tableView.cellAtIndexPath( indexPath ) as BusinessLicenseSeasonCell;
        cell.accessoryType = i >= 0 ? UITableViewCellAccessoryType.None : UITableViewCellAccessoryType.Checkmark;
    }
}
